import React, { useEffect, useMemo } from "react";
import cn from "classnames";
import { useField, useFormikContext } from "formik";
import format from "date-fns/format";
import isAfter from "date-fns/isAfter";
import isEqual from "date-fns/isEqual";
import DatePicker, { registerLocale } from "react-datepicker";
import { ru } from "date-fns/locale";

import InputError, { ERROR_PLACE } from "components/InputsField/InputError";
import Label from "components/InputsField/Label";

import { FORMAT_DATE_IN_FORM } from "constants/time";

import styles from "./styles.module.scss";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ru", ru);

export default function FormikDateInput({
  label,
  isRequired,
  errorMode,
  labelClasses,
  name,
  minDate,
  maxDate,
  ...props
}) {
  const [field, meta] = useField(name);
  const minDateFormat = useMemo(
    () =>
      minDate
        ? new Date(format(minDate, FORMAT_DATE_IN_FORM))
        : minDate,
    [minDate]
  );

  const fieldValueFormat = useMemo(
    () =>
      field.value
        ? new Date(format(new Date(field.value), FORMAT_DATE_IN_FORM))
        : null,
    [field.value]
  );

  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    if (
      fieldValueFormat &&
      minDateFormat &&
      isAfter(minDateFormat, fieldValueFormat) &&
      !isEqual(fieldValueFormat, minDateFormat)
    ) {
      setFieldValue(name, null);
    }
  }, [fieldValueFormat, minDateFormat, name, setFieldValue]);

  const onChange = (value) => {
    if (value) {
      setFieldValue(name, format(value, FORMAT_DATE_IN_FORM));
    }
  };

  return (
    <div className={cn(styles.base, labelClasses)}>
      <Label name={name} label={label} required={isRequired} />
      <DatePicker
        dateFormat="dd.MM.yyyy"
        locale="ru"
        calendarStartDay={1}
        name={name}
        id={name}
        wrapperClassName={styles.wrapper}
        className={cn(styles.input, {
          [styles["input--warn"]]: meta.touched && meta.error,
        })}
        minDate={minDate || null}
        maxDate={maxDate || null}
        selected={field.value && new Date(field.value)}
        onChange={onChange}
        clearIcon={null}
        autoComplete="off"
        {...props}
      />
      {meta.touched && meta.error ? (
        <InputError
          message={meta.error}
          mode={errorMode || ERROR_PLACE.bottomLeft}
        />
      ) : null}
    </div>
  );
}
